import React, { useEffect, useState } from "react";
import { useRecordContext, useNotify, useGetList, Button } from "react-admin";
import { httpClient } from "../httpClient";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import MuiButton from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { FermentAutomation, AutomationLogEntry, ServerStep } from "./core";
import { useRefresh } from "react-admin";
import SaveIcon from "@mui/icons-material/Save";

// Protocol selector autocomplete for UseProtocol modal
const ProtocolsAutocomplete = ({ onSelectionChange }) => {
  const { data, isLoading, error } = useGetList("automation_protocols", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  if (isLoading) return <p>Loading protocols...</p>;
  if (error) return <p>Error loading protocols</p>;
  if (!data) return null;

  return (
    <Autocomplete
      options={data}
      getOptionLabel={(obj) => obj.name || obj.id}
      renderInput={(params) => (
        <TextField {...params} label="Select a protocol" />
      )}
      onChange={(_event, value) => {
        if (value) {
          onSelectionChange(value);
        } else {
          onSelectionChange(null);
        }
      }}
    />
  );
};

// UseProtocolButton — opens a modal to select and apply an AutomationProtocol
const UseProtocolButton = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);
  const [selectedProtocol, setSelectedProtocol] = React.useState<any>(null);

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  // Only show when there are no automation log entries (ferment not yet started)
  const logEntries = record.automation?.log || [];
  if (logEntries.length > 0) return null;

  const handleOpen = () => {
    setOpen(true);
    setRunning(false);
    setSelectedProtocol(null);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const handleApply = () => {
    if (!selectedProtocol?.automation?.steps) return;

    setRunning(true);
    httpClient(
      `/api/v2/c/${record.customerID}/ferments/${record.id}/automation`,
      {
        method: "PUT",
        body: JSON.stringify({ steps: selectedProtocol.automation.steps }),
      },
    )
      .then((resp) => {
        if (resp.ok) {
          notify("Protocol applied successfully", { type: "success" });
        } else {
          notify("Failed to apply protocol", { type: "error" });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify("Failed to apply protocol", { type: "error" });
        console.error(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  return (
    <>
      <Button
        label="Use Protocol"
        variant="outlined"
        size="medium"
        onClick={handleOpen}
        disabled={running}
      />
      <Dialog open={open} onClose={handleCancel}>
        <DialogTitle>Apply Automation Protocol</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a protocol to copy its automation steps into this ferment:
          </DialogContentText>
          <DialogContentText>
            <ProtocolsAutocomplete onSelectionChange={setSelectedProtocol} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleApply}
            disabled={!selectedProtocol || running}
          >
            Apply
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Standalone version — manages state, data loading, save/reset
// Used in ferments.tsx where the component owns its lifecycle
export const FermentAutomationStandalone = ({
  readonly = false,
}: {
  readonly?: boolean;
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [steps, setSteps] = useState<ServerStep[]>([]);
  const [initialSteps, setInitialSteps] = useState<ServerStep[]>([]);
  const [saving, setSaving] = useState(false);
  const [automationLog, setAutomationLog] = useState<AutomationLogEntry[]>([]);

  if (!!record.endTime) readonly = true;

  useEffect(() => {
    if (!record) return;

    const recordedSteps = record.automation?.steps || [];
    const logEntries = record.automation?.log || [];
    const stepsToSet =
      recordedSteps.length === 0
        ? [
            {
              densityThreshold: 0,
              densityThresholdIsRelative: true,
              settings: [],
              conditions: [],
              checkpoint: { enabled: false, description: "" },
            },
          ]
        : recordedSteps;

    setSteps(stepsToSet);
    setInitialSteps(stepsToSet);
    setAutomationLog(logEntries);
  }, [record]);

  if (!record) return null;

  const handleSave = () => {
    setSaving(true);
    httpClient(
      `/api/v2/c/${record.customerID}/ferments/${record.id}/automation`,
      {
        method: "PUT",
        body: JSON.stringify({ steps }),
      },
    )
      .then((resp) => {
        setSaving(false);
        if (resp.ok) {
          notify("Automation steps saved successfully", { type: "success" });
          refresh();
          setInitialSteps(steps);
        } else {
          notify("Failed to save automation steps", { type: "error" });
        }
      })
      .catch((error) => {
        setSaving(false);
        notify("Failed to save automation steps", { type: "error" });
        console.error("Save error:", error);
      });
  };

  const handleReset = () => {
    setSteps(initialSteps);
  };

  const handleClearCheckpoint = (
    _stepIndex: number,
    checkpointDescription: string,
  ) => {
    httpClient(
      `/api/v2/c/${record.customerID}/ferments/${record.id}/clear_checkpoint`,
      {
        method: "POST",
        body: JSON.stringify({ description: checkpointDescription }),
      },
    )
      .then((resp) => {
        if (resp.ok) {
          notify("Checkpoint cleared", { type: "success" });
          refresh();
        } else {
          notify("Failed to clear checkpoint", { type: "error" });
        }
      })
      .catch((error) => {
        notify("Failed to clear checkpoint", { type: "error" });
        console.error("Clear checkpoint error:", error);
      });
  };

  const hasChanges = JSON.stringify(steps) !== JSON.stringify(initialSteps);

  return (
    <>
      <FermentAutomation
        value={steps}
        onChange={setSteps}
        automationLog={automationLog}
        onClearCheckpoint={handleClearCheckpoint}
        readonly={readonly}
      />
      {!readonly && (
        <Box
          style={{
            marginLeft: -16,
            marginRight: -16,
            marginBottom: -8,
            padding: 14,
            paddingLeft: 24,
            background: "#e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            <MuiButton
              variant="contained"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{ marginRight: 8 }}
            >
              <SaveIcon fontSize="small" style={{ marginRight: 8 }} />
              Save
            </MuiButton>
            <MuiButton
              variant="outlined"
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              Reset
            </MuiButton>
          </div>
          <UseProtocolButton />
        </Box>
      )}
    </>
  );
};
